import { z } from 'zod';
import { IdSchema, ListQuerySchema, PageInfoSchema, TimestampSchema, VersionSchema, } from './common.js';
export const JobFileLifecycleSchema = z.enum(['intake', 'active', 'closed', 'canceled']);
/**
 * Durable operational identity from intake through delivery (specification 6.1, 6.3). Leads
 * and projects expose its context; they never copy it.
 */
export const JobFileSchema = z.strictObject({
    id: IdSchema,
    organizationId: IdSchema,
    referenceNumber: z.string(),
    lifecycle: JobFileLifecycleSchema,
    customerAccountId: IdSchema.nullable(),
    propertyId: IdSchema.nullable(),
    openedAt: TimestampSchema,
    closedAt: TimestampSchema.nullable(),
    version: VersionSchema,
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});
export const CreateJobFileRequestSchema = z.strictObject({
    customerAccountId: IdSchema.nullable().default(null),
    propertyId: IdSchema.nullable().default(null),
});
export const UpdateJobFileRequestSchema = CreateJobFileRequestSchema.partial().extend({
    version: VersionSchema,
});
/** Lifecycle moves are named commands; a reason is kept in the audit trail. */
export const JobFileLifecycleCommandSchema = z.strictObject({
    reason: z.string().trim().max(500).optional(),
    version: VersionSchema,
});
export const JobFileListQuerySchema = ListQuerySchema.extend({
    lifecycle: JobFileLifecycleSchema.optional(),
    propertyId: IdSchema.optional(),
    customerAccountId: IdSchema.optional(),
});
export const JobFileListSchema = z.strictObject({
    items: z.array(JobFileSchema),
    page: PageInfoSchema,
});
/** Area names compare case-insensitively with collapsed whitespace: "Basement " and "basement" are one area. */
export function normalizeAreaName(value) {
    return value.trim().replace(/\s+/g, ' ').toLowerCase().slice(0, 120);
}
/**
 * An affected area of a job file (a room, floor, or zone). Deployments, readings, and the
 * drying log refer to areas by identity rather than free text (specification 8.11, 8.18).
 */
export const JobFileAreaSchema = z.strictObject({
    id: IdSchema,
    organizationId: IdSchema,
    jobFileId: IdSchema,
    name: z.string(),
    level: z.string().nullable(),
    notes: z.string().nullable(),
    sortOrder: z.number().int(),
    active: z.boolean(),
    version: VersionSchema,
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});
export const CreateJobFileAreaRequestSchema = z.strictObject({
    name: z.string().trim().min(1).max(120),
    level: z.string().trim().max(60).nullable().default(null),
    notes: z.string().trim().max(1000).nullable().default(null),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
});
export const UpdateJobFileAreaRequestSchema = z.strictObject({
    name: z.string().trim().min(1).max(120).optional(),
    level: z.string().trim().max(60).nullable().optional(),
    notes: z.string().trim().max(1000).nullable().optional(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
    active: z.boolean().optional(),
    version: VersionSchema,
});
export const JobFileAreaListQuerySchema = z.strictObject({
    includeInactive: z.stringbool().default(false),
});
export const JobFileAreaListSchema = z.strictObject({ items: z.array(JobFileAreaSchema) });
